import { x25519 } from '@noble/curves/ed25519';
import { argon2id } from 'hash-wasm';
import {
  decodeBase64Url,
  decryptAesGcm,
  encodeBase64Url,
  encryptAesGcm,
  importAesKey,
  packEncryptedBlob,
  randomBytes,
  sha256,
  unpackEncryptedBlob,
} from './primitives';
import {
  assertPasswordPolicy,
  defaultPolicyForTarget,
  type PasswordPolicy,
  type ValidatePasswordOptions,
} from './passwordPolicy';

const VAULT_VERSION_KEY = 'shareweb:vaultVersion:';

export interface VaultPlaintext {
  vaultVersion: number;
  userPriv?: string;
  knownUserPubChecksums: Record<string, string>;
  infoReplyPriv?: Record<string, string>;
}

export interface VaultSetupResult {
  vaultBlob: Uint8Array;
  vaultSalt: Uint8Array;
  vkWrapPassphrase: Uint8Array;
  userPublicKey: Uint8Array;
  userPrivateKey: Uint8Array;
  vaultKey: Uint8Array;
}

export async function deriveVaultPassphraseKey(
  passphrase: string,
  salt: Uint8Array,
): Promise<Uint8Array> {
  const derived = await argon2id({
    password: passphrase,
    salt,
    parallelism: 1,
    iterations: 3,
    memorySize: 65536,
    hashLength: 32,
    outputType: 'binary',
  });

  return derived instanceof Uint8Array ? derived : new Uint8Array(derived);
}

export async function setupVault(
  passphrase: string,
  policy: PasswordPolicy = defaultPolicyForTarget('vaultPassphrase'),
  options: ValidatePasswordOptions = {},
): Promise<VaultSetupResult> {
  assertPasswordPolicy(passphrase, policy, options);
  const vaultSalt = randomBytes(16);
  const vaultKey = randomBytes(32);
  const passphraseKey = await deriveVaultPassphraseKey(passphrase, vaultSalt);
  const userPrivateKey = x25519.utils.randomPrivateKey();
  const userPublicKey = x25519.getPublicKey(userPrivateKey);

  const plaintext: VaultPlaintext = {
    vaultVersion: 1,
    userPriv: encodeBase64Url(userPrivateKey),
    knownUserPubChecksums: {},
  };

  const plaintextBytes = new TextEncoder().encode(JSON.stringify(plaintext));
  const aesKey = await importAesKey(vaultKey);
  const encryptedPayload = await encryptAesGcm(aesKey, plaintextBytes);
  const vaultBlob = packEncryptedBlob(encryptedPayload);

  const vkWrapAesKey = await importAesKey(passphraseKey);
  const vkWrapPayload = await encryptAesGcm(vkWrapAesKey, vaultKey);
  const vkWrapPassphrase = packEncryptedBlob(vkWrapPayload);

  return {
    vaultBlob,
    vaultSalt,
    vkWrapPassphrase,
    userPublicKey,
    userPrivateKey,
    vaultKey,
  };
}

function isVaultPassphraseInvalidCause(cause: unknown): boolean {
  if (
    (typeof DOMException !== 'undefined' && cause instanceof DOMException && cause.name === 'OperationError')
    || (cause instanceof Error && cause.name === 'OperationError')
  ) {
    return true;
  }

  if (cause instanceof SyntaxError) {
    return true;
  }

  if (cause instanceof Error) {
    return cause.message === 'invalid_encrypted_blob'
      || cause.message === 'unsupported_blob_version';
  }

  return false;
}

/** Map decrypt/unwrap failures to a stable unlock code for UI/support. */
export function mapVaultUnlockCryptoFailure(cause: unknown): never {
  if (cause instanceof Error
    && (cause.message === 'vault_version_mismatch' || cause.message === 'vault_missing_user_priv')) {
    throw cause;
  }

  if (isVaultPassphraseInvalidCause(cause)) {
    throw new Error('vault_passphrase_invalid');
  }

  throw cause;
}

export async function unlockVaultWithPassphrase(
  passphrase: string,
  vaultSalt: Uint8Array,
  vkWrapPassphrase: Uint8Array,
  vaultBlob: Uint8Array,
  expectedServerVersion: number,
): Promise<{ vaultKey: Uint8Array; plaintext: VaultPlaintext; userPrivateKey: Uint8Array }> {
  try {
    const passphraseKey = await deriveVaultPassphraseKey(passphrase, vaultSalt);
    const vkWrapAesKey = await importAesKey(passphraseKey);
    const vaultKey = await decryptAesGcm(vkWrapAesKey, unpackEncryptedBlob(vkWrapPassphrase));
    const aesKey = await importAesKey(vaultKey);
    const plaintextBytes = await decryptAesGcm(aesKey, unpackEncryptedBlob(vaultBlob));
    const plaintext = JSON.parse(new TextDecoder().decode(plaintextBytes)) as VaultPlaintext;

    if (plaintext.vaultVersion !== expectedServerVersion) {
      throw new Error('vault_version_mismatch');
    }

    if (typeof plaintext.userPriv !== 'string' || plaintext.userPriv.length === 0) {
      throw new Error('vault_missing_user_priv');
    }

    return { vaultKey, plaintext, userPrivateKey: decodeBase64Url(plaintext.userPriv) };
  } catch (cause) {
    mapVaultUnlockCryptoFailure(cause);
  }
}

export async function rewrapVaultPassphrase(
  currentPassphrase: string,
  newPassphrase: string,
  vaultSalt: Uint8Array,
  vkWrapPassphrase: Uint8Array,
  vaultBlob: Uint8Array,
  expectedServerVersion: number,
  policy: PasswordPolicy = defaultPolicyForTarget('vaultPassphrase'),
  options: ValidatePasswordOptions = {},
): Promise<Uint8Array> {
  assertPasswordPolicy(newPassphrase, policy, options);
  const { vaultKey } = await unlockVaultWithPassphrase(
    currentPassphrase,
    vaultSalt,
    vkWrapPassphrase,
    vaultBlob,
    expectedServerVersion,
  );

  const newPassphraseKey = await deriveVaultPassphraseKey(newPassphrase, vaultSalt);
  const vkWrapAesKey = await importAesKey(newPassphraseKey);
  return packEncryptedBlob(await encryptAesGcm(vkWrapAesKey, vaultKey));
}

export async function decryptVaultPlaintext(
  vaultKey: Uint8Array,
  vaultBlob: Uint8Array,
): Promise<VaultPlaintext> {
  const aesKey = await importAesKey(vaultKey);
  const plaintextBytes = await decryptAesGcm(aesKey, unpackEncryptedBlob(vaultBlob));
  return JSON.parse(new TextDecoder().decode(plaintextBytes)) as VaultPlaintext;
}

export async function encryptVaultPlaintext(
  vaultKey: Uint8Array,
  plaintext: VaultPlaintext,
): Promise<Uint8Array> {
  const aesKey = await importAesKey(vaultKey);
  const plaintextBytes = new TextEncoder().encode(JSON.stringify(plaintext));
  const encryptedPayload = await encryptAesGcm(aesKey, plaintextBytes);
  return packEncryptedBlob(encryptedPayload);
}

export async function checksumUserPublicKey(userPublicKey: Uint8Array): Promise<string> {
  const digest = await sha256(userPublicKey);
  return Array.from(digest)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export function getStoredVaultVersion(userId: string): number | null {
  const value = localStorage.getItem(`${VAULT_VERSION_KEY}${userId}`);
  if (!value) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function clearStoredVaultVersion(userId: string): void {
  localStorage.removeItem(`${VAULT_VERSION_KEY}${userId}`);
}

export function setStoredVaultVersion(userId: string, version: number): void {
  if (!Number.isFinite(version) || version <= 0) {
    return;
  }

  localStorage.setItem(`${VAULT_VERSION_KEY}${userId}`, String(Math.trunc(version)));
}

export function syncVaultVersionHighWaterMark(
  userId: string,
  serverVersion: number,
): 'none' | 'synced' | 'regression' {
  const server = normalizeVaultVersion(serverVersion);
  if (server <= 0) {
    return 'none';
  }

  const stored = getStoredVaultVersion(userId);
  if (stored === null) {
    return 'none';
  }

  if (server >= stored) {
    if (server !== stored) {
      setStoredVaultVersion(userId, server);
    }
    return 'synced';
  }

  return 'regression';
}

export function normalizeVaultVersion(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

export function hasVaultVersionRegression(
  userId: string,
  serverVersion: number,
): boolean {
  return syncVaultVersionHighWaterMark(userId, serverVersion) === 'regression';
}
